// MobileLive — aba AO VIVO da superfície MOBILE (touch). Parede A5.
//
// Chips de categoria (horizontais, tapáveis) + LISTA VERTICAL de canais
// (logo + nome + programa atual via EPG). Tap num canal → player direto (canal
// ao vivo não tem tela de detalhe; vai pro MobilePlayer kind='channel'). Sem
// hero com áudio.
//
// Reusa a camada de dados da LiveScreen da TV: api.getCategories (chips),
// api.getAllChannels ("Todos") / api.getChannels(catId) (categoria) e
// api.getEpgShortBatch (programa atual dos canais visíveis). A apresentação é
// simplificada vs TV (sem agrupamento de família/buckets canônicos): lista
// direta, suficiente pro toque. Keys scopadas via useProviderQueryKey.

import { useTranslation } from '@app-iptv/i18n'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { api, ApiError } from '../api'
import { Poster } from '../components/Poster'
import { useProviderQueryKey } from '../hooks/useScopedQueryKey'

import { buildCatalogChips } from './mobile-catalog'
import styles from './MobileCatalog.module.css'
import { MobileChips } from './MobileChips'
import type { MobileHomeItem } from './MobileHome'
import { MobileEmpty, MobileLoading, MobileNoSource } from './MobileStates'

/** Cap de canais com EPG resolvido por vez (batch backend tem cap ~100). Os
 *  demais aparecem sem "programa agora" — sem N requests. */
const EPG_CAP = 80

function isNoProvider(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'no_provider'
}

export interface MobileLiveProps {
  /** Tap num canal → player direto (kind='channel'). */
  onOpenItem: (item: MobileHomeItem) => void
  onConfigureSource?: (() => void) | undefined
}

export function MobileLive({ onOpenItem, onConfigureSource }: MobileLiveProps): JSX.Element {
  const { t } = useTranslation()
  const [activeCat, setActiveCat] = useState<string | null>(null)

  const categoriesQ = useQuery({
    queryKey: useProviderQueryKey(['live-categories']),
    queryFn: () => api.getCategories(),
    staleTime: 30 * 60 * 1000,
    retry: (count, err) => !isNoProvider(err) && count < 2,
  })

  const channelsQ = useQuery({
    queryKey: useProviderQueryKey(['mobile-live-channels', activeCat ?? 'all']),
    queryFn: () => (activeCat ? api.getChannels(activeCat) : api.getAllChannels()),
    staleTime: 10 * 60 * 1000,
    retry: (count, err) => !isNoProvider(err) && count < 2,
  })

  const channels = useMemo(() => channelsQ.data ?? [], [channelsQ.data])

  // EPG "agora" só pros primeiros N canais (cap do batch). Os demais ficam sem.
  const epgIds = useMemo(() => channels.slice(0, EPG_CAP).map((c) => c.id), [channels])
  const epgQ = useQuery({
    queryKey: useProviderQueryKey(['mobile-live-epg', epgIds.join(',')]),
    queryFn: () => api.getEpgShortBatch(epgIds),
    enabled: epgIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })
  const epg = epgQ.data ?? {}

  const chips = useMemo(
    () => buildCatalogChips(categoriesQ.data ?? [], t('common.all')),
    [categoriesQ.data, t],
  )

  const noProvider = isNoProvider(categoriesQ.error) || isNoProvider(channelsQ.error)
  if (noProvider) {
    return <MobileNoSource onConfigureSource={onConfigureSource} />
  }

  return (
    <div className={styles.screen} data-screen="live">
      <MobileChips
        chips={chips}
        activeId={activeCat}
        onSelect={setActiveCat}
        ariaLabel={t('nav.live')}
      />

      {channelsQ.isLoading && channels.length === 0 ? (
        <MobileLoading />
      ) : channels.length === 0 ? (
        <MobileEmpty title={t('empty.noChannelsTitle')} text={t('empty.noChannelsDesc')} />
      ) : (
        <div className={styles.channelList} data-list="channels">
          {channels.map((c) => {
            const now = epg[c.id]?.now?.title ?? null
            return (
              <button
                key={c.id}
                type="button"
                className={styles.channelRow}
                data-channel={c.id}
                onClick={() =>
                  onOpenItem({ id: c.id, kind: 'channel', title: c.name, poster: c.logo })
                }
              >
                <span className={styles.channelLogo}>
                  <Poster
                    src={c.logo}
                    kind="logo"
                    alt={c.name}
                    fallbackText={c.name}
                    className={styles.channelLogoImg}
                  />
                </span>
                <span className={styles.channelInfo}>
                  <span className={styles.channelName}>{c.name}</span>
                  {now && <span className={styles.channelEpg}>{now}</span>}
                </span>
                <span className={styles.channelPlay} aria-hidden="true">
                  ▶
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
